import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import {Box} from '@material-ui/core';
import moment from 'moment';
import cn from 'classnames';
import {toast} from 'react-toastify';
import {
  ReportButtonOutline,
  ReportButtonPrimary
} from 'routes/App/reports/ReportsTable/styles';
import Headline from 'components/Headline/styles';
import Loading from 'components/shared/Utils/Loading';
import {
  CLEAR_REPORT_DATA,
  EXPORT_REPORTS,
  GENERATE_REPORT,
  GENERATE_SPECIAL_ORDERS_REPORT,
  RECALCULATE_COMMISSION_SAVED,
  SET_DATEPICKER_INDEX,
  SET_REPORT_TYPE,
  SET_REPORT_VISITED_STATISTIC,
  SET_REPORTS_DATE_INTERVAL,
  SET_SELF_DELIVERY_DATE_FILTER,
  SET_TIME_PERIOD
} from 'store/reports/action';
import {DashboardContent} from 'components/DashboardContent/styles';
import {DashboardHeader} from 'components/DashboardHeader/styles';
import {DashboardInner} from 'components/DashboardInner/styles';
import IconExportReport from 'assets/IconExportReport';
import IconSettingsWhite from 'assets/IconSettingsWhite';
import ReportsDataPicker from 'routes/App/reports/ReportsDataPicker/ReportsDataPicker';
import ReportsTable from 'routes/App/reports/ReportsTable/ReportsTable';
import {
  FIELDS_CUSTOMERS,
  FIELDS_SALES_SUMMARY,
  FIELDS_PAYOUT_SUMMARY,
  FIELDS_BOOK_A_DRIVER,
  FIELDS_BYOC,
  FIELDS_SELF_DELIVERY_SUMMARY,
  FIELDS_SELF_DELIVERY_SUMMARY_SPLIT,
  FIELDS_SELF_DELIVERY_SUMMARY_BY_CHARGE_DATE,
  FIELDS_SELF_DELIVERY_SUMMARY_SPLIT_BY_CHARGE_DATE,
  TOTAL_SALES_SUMMARY,
  TOTAL_PAYOUT_SUMMARY,
  TOTAL_SELF_DELIVERY_SUMMARY,
  TOTAL_SELF_DELIVERY_SUMMARY_SPLIT,
  TOTAL_SELF_DELIVERY_SUMMARY_BY_CHARGE_DATE,
  TOTAL_SELF_DELIVERY_SUMMARY_SPLIT_BY_CHARGE_DATE,
  FIELDS_REFUNDS_SUMMARY,
  TOTAL_REFUNDS_SUMMARY,
  FIELDS_FEEDBACK_SUMMARY,
  TOTAL_CUSTOMERS,
  REPORT_TYPES,
  REPORT_DATEPICKER_INDEX,
  REPORT_TIME_PERIOD_TITLES,
  REPORT_TYPE_TITLES,
  POSTHOG_EVENT_NAMES,
  SELF_DELIVERY_DATE_FILTER,
  SELF_DELIVERY_DATE_FILTER_TITLES,
  FIELDS_BAD_SUMMARY,
  TOTAL_BAD_SUMMARY,
  FIELDS_DISCOUNT_SUMMARY,
  TOTAL_DISCOUNT_SUMMARY,
  FIELDS_CASHBACK_USAGE,
  TOTAL_CASHBACK_USAGE,
  FIELDS_SALES_BREAKDOWN_SUMMARY,
  TOTAL_SALES_BREAKDOWN_SUMMARY,
  FIELDS_SELF_DELIVERY_INVOICES,
  SELF_DELIVERY_INVOICES_PERIOD_TITLES,
  getSelfDeliveryInvoicesDateRange
} from 'helpers/constants/reports/index';
import ModalReportVideo from 'routes/App/reports/modals/ReportVideo';
import PlatformCommissionsSettingsModal from 'routes/App/reports/modals/PlatformCommissionsSettings';
import BulkExportModal from 'routes/App/reports/modals/BulkExport';
import BulkExportSentModal from 'routes/App/reports/modals/BulkExportSent';
import {TopLineNav} from 'components/layout';
import LinkBack from 'components/LinkBack/styles';
import useDidMountEffect from 'hooks/useDidMountEffect';
import {getSessionStorageItem} from 'helpers/sessionStorage';
import SESSION_SAUCE_EMPLOYEE_NAME from 'helpers/sessionStorage/constants';
import {
  FEATURE_RESTRICTION_IDS,
  STRIPE_ACCOUNT_RESTRICTION_NAMES
} from 'helpers/constants/restrictions';
import FullScreenLoading from 'components/shared/Utils/FullScreenLoading/FullScreenLoading';
import SpecialOrdersReport from 'routes/App/reports/ReportsTable/SpecialOrdersReport';
import {getPlatformCommissions} from 'helpers/services/api/hubspot';

const selectionsMatch = (left = [], right = []) => {
  const current = left || [];
  const applied = right || [];
  return (
    current.length === applied.length &&
    current.every((value) => applied.includes(value))
  );
};

const ReportsContainer = () => {
  const dispatch = useDispatch();

  const location = useLocation();
  const history = useHistory();
  const [previousLocationState, setPreviousLocationState] = useState(null);
  const {
    dataLoading,
    generationLoading,
    exportLoading,
    data,
    reportType,
    totalRow,
    dataCount,
    dateRange,
    quantityData,
    specialOrdersReportId,
    timePeriod,
    selfDeliveryDateFilter,
    selfDeliveryInvoicesPeriod,
    salesBreakdownChannels,
    salesBreakdownFulfillments
  } = useSelector((state) => state.reportsReducer);
  const Timezone = useSelector((state) => state.user.activeLocation.Timezone);
  const isCheckStripeAccountExpress = useSelector((state) =>
    state.user.locations.find(
      (el) =>
        el.StripeAccountType ===
        STRIPE_ACCOUNT_RESTRICTION_NAMES.CONNECTED_EXPRESS
    )
  );

  const employeeView = getSessionStorageItem(SESSION_SAUCE_EMPLOYEE_NAME);
  const activeLocationFeatures = useSelector(
    (state) => state.user.activeLocation?.AllowedServiceFeatures
  );
  const selfDeliverySplitFeeTipEnabled =
    activeLocationFeatures?.includes(
      FEATURE_RESTRICTION_IDS.DASHBOARD_SELF_DELIVERY_REPORT_SPLIT_FEE_TIP
    ) ?? false;

  // Export/Settings act on generated data, so the selected view must match it
  const selfDeliveryViewMismatch =
    reportType === REPORT_TYPES.SELF_DELIVERY_SUMMARY &&
    selfDeliveryDateFilter !== data.reportDateFilter;
  const selfDeliveryFields = {
    [SELF_DELIVERY_DATE_FILTER.ORDER_DATE]: selfDeliverySplitFeeTipEnabled
      ? FIELDS_SELF_DELIVERY_SUMMARY_SPLIT
      : FIELDS_SELF_DELIVERY_SUMMARY,
    [SELF_DELIVERY_DATE_FILTER.CHARGE_DATE]: selfDeliverySplitFeeTipEnabled
      ? FIELDS_SELF_DELIVERY_SUMMARY_SPLIT_BY_CHARGE_DATE
      : FIELDS_SELF_DELIVERY_SUMMARY_BY_CHARGE_DATE
  };
  const selfDeliveryTotalFields = {
    [SELF_DELIVERY_DATE_FILTER.ORDER_DATE]: selfDeliverySplitFeeTipEnabled
      ? TOTAL_SELF_DELIVERY_SUMMARY_SPLIT
      : TOTAL_SELF_DELIVERY_SUMMARY,
    [SELF_DELIVERY_DATE_FILTER.CHARGE_DATE]: selfDeliverySplitFeeTipEnabled
      ? TOTAL_SELF_DELIVERY_SUMMARY_SPLIT_BY_CHARGE_DATE
      : TOTAL_SELF_DELIVERY_SUMMARY_BY_CHARGE_DATE
  };

  const fieldSelector = {
    0: FIELDS_SALES_SUMMARY,
    1: FIELDS_CUSTOMERS,
    2: FIELDS_PAYOUT_SUMMARY,
    3: selfDeliveryFields[selfDeliveryDateFilter],
    4: FIELDS_BOOK_A_DRIVER,
    5: FIELDS_REFUNDS_SUMMARY,
    6: FIELDS_FEEDBACK_SUMMARY,
    8: FIELDS_BAD_SUMMARY,
    9: FIELDS_DISCOUNT_SUMMARY,
    10: FIELDS_CASHBACK_USAGE,
    [REPORT_TYPES.SALES_BREAKDOWN_SUMMARY]: FIELDS_SALES_BREAKDOWN_SUMMARY,
    [REPORT_TYPES.SELF_DELIVERY_INVOICES]: FIELDS_SELF_DELIVERY_INVOICES
  };
  const totalFieldSelector = {
    0: TOTAL_SALES_SUMMARY,
    1: TOTAL_CUSTOMERS,
    2: TOTAL_PAYOUT_SUMMARY,
    3: selfDeliveryTotalFields[selfDeliveryDateFilter],
    5: TOTAL_REFUNDS_SUMMARY,
    8: TOTAL_BAD_SUMMARY,
    9: TOTAL_DISCOUNT_SUMMARY,
    10: TOTAL_CASHBACK_USAGE,
    [REPORT_TYPES.SALES_BREAKDOWN_SUMMARY]: TOTAL_SALES_BREAKDOWN_SUMMARY
    //    6: TOTAL_FEEDBACK_SUMMARY
  };

  // Captured once on mount — location.search may be cleared by history.replace before effects re-run
  const initialLocationSearchRef = useRef(location.search);

  const [initialReq, setInitialReq] = useState(false);
  const [fields, setFields] = useState(fieldSelector[reportType]);
  const [totalField, setTotalField] = useState(totalFieldSelector[reportType]);

  // The type picker clears the previous report (CLEAR_REPORT_DATA), so the empty
  // table shows the selected report's columns, not the old ones.
  useDidMountEffect(() => {
    setFields(fieldSelector[reportType]);
    setTotalField(totalFieldSelector[reportType]);
  }, [reportType]);
  const [includeModifiers, setIncludeModifiers] = useState(true);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsButtonLoading, setSettingsButtonLoading] = useState(false);
  const [initialCommissionsForModal, setInitialCommissionsForModal] = useState(
    null
  );
  const locationId = useSelector((state) => state.user.locationId);
  const {activeLocation, userData} = useSelector((state) => state.user);
  const userLocations = useSelector((state) => state.user.locations);
  const [bulkExportOpen, setBulkExportOpen] = useState(false);
  const [bulkExportResult, setBulkExportResult] = useState(null);
  const bulkExportEnabled =
    (activeLocationFeatures?.includes(
      FEATURE_RESTRICTION_IDS.SALES_REPORTS_BULK_EXPORT
    ) ??
      false) &&
    Array.isArray(userLocations) &&
    userLocations.length >= 2;
  const reportLocationId = useSelector(
    (state) => state.reportsReducer.reportLocationId
  );

  // The generated report belongs to one location; after a location switch
  // (here or on another page) it must not stay on screen or be exportable.
  useEffect(() => {
    if (reportLocationId && locationId && reportLocationId !== locationId) {
      dispatch(CLEAR_REPORT_DATA());
    }
  }, [locationId, reportLocationId]);

  const handleSettingsClick = useCallback(() => {
    if (!locationId) return;
    setSettingsButtonLoading(true);
    getPlatformCommissions(locationId)
      .then((response) => {
        if (response?.data) {
          const transformedData = response.data.map((item) => ({
            platform: item.platform,
            platformCommissionSelfDelivery: item.platformCommissionSelfDelivery,
            platformCommissionPlatformDelivery:
              item.platformCommissionPlatformDelivery
          }));
          setInitialCommissionsForModal(transformedData);
          setSettingsModalOpen(true);
        }
      })
      .catch((error) => {
        console.error('Error loading platform commissions:', error);
        toast.error('Failed to load platform commissions');
      })
      .finally(() => {
        setSettingsButtonLoading(false);
      });
  }, [locationId]);

  const defaultDateArr = [
    moment()
      .utcOffset(Timezone, false)
      .utc()
      .subtract(1, 'week')
      .startOf('isoWeek')
      .format('YYYY-MM-DD'),
    moment()
      .utcOffset(Timezone, false)
      .utc()
      .subtract(1, 'week')
      .endOf('isoWeek')
      .format('YYYY-MM-DD')
  ];

  const getDateRangeForPeriod = (periodNum) => {
    const d = () => moment().utcOffset(Timezone, false).utc();
    switch (periodNum) {
      case REPORT_DATEPICKER_INDEX.ALL_PERIOD:
        return [null, null];
      case REPORT_DATEPICKER_INDEX.TODAY:
        return [
          d().startOf('day').format('YYYY-MM-DD'),
          d().endOf('day').format('YYYY-MM-DD')
        ];
      case REPORT_DATEPICKER_INDEX.THIS_WEEK:
        return [
          d().startOf('isoWeek').format('YYYY-MM-DD'),
          d().endOf('isoWeek').format('YYYY-MM-DD')
        ];
      case REPORT_DATEPICKER_INDEX.LAST_WEEK:
        return defaultDateArr;
      case REPORT_DATEPICKER_INDEX.THIS_MONTH:
        return [
          d().startOf('month').format('YYYY-MM-DD'),
          d().endOf('month').format('YYYY-MM-DD')
        ];
      case REPORT_DATEPICKER_INDEX.LAST_MONTH:
        return [
          d().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
          d().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
        ];
      default:
        return defaultDateArr;
    }
  };

  const sendEventToPostHog = (eventName, total) => {
    if (window?.posthog) {
      const isInvoices = reportType === REPORT_TYPES.SELF_DELIVERY_INVOICES;
      let customDateRange = isInvoices
        ? getSelfDeliveryInvoicesDateRange(selfDeliveryInvoicesPeriod, Timezone)
        : dateRange;
      if (!isInvoices && dateRange[0] === '' && dateRange[1] === '') {
        customDateRange = defaultDateArr;
      }
      window?.posthog?.capture(eventName, {
        report_type: REPORT_TYPE_TITLES[reportType],
        time_period_type: isInvoices
          ? SELF_DELIVERY_INVOICES_PERIOD_TITLES[selfDeliveryInvoicesPeriod]
          : REPORT_TIME_PERIOD_TITLES[timePeriod],
        time_period_start: customDateRange[0],
        time_period_end: customDateRange[1],
        ...(reportType === REPORT_TYPES.SELF_DELIVERY_SUMMARY && {
          view_by: SELF_DELIVERY_DATE_FILTER_TITLES[selfDeliveryDateFilter]
        }),
        ...(eventName === POSTHOG_EVENT_NAMES.REPORTS_GENERATED && {
          result_state: total > 0 ? 'with_results' : 'no_results',
          records_count: total
        }),
        location_id: activeLocation?._id,
        location_name: activeLocation?.BusinessName || activeLocation?.FullName,
        user_id: userData?._id,
        user_email: userData?.email,
        timestamp: moment().utc().format()
      });
    }
  };

  const generateButtonHandler = () => {
    sendEventToPostHog(POSTHOG_EVENT_NAMES.REPORTS_GENERATE_CLICKED, null);
    if (reportType === REPORT_TYPES.SPECIAL_ORDERS) {
      dispatch(
        GENERATE_SPECIAL_ORDERS_REPORT({
          includeModifiers
        })
      );
    } else {
      setFields(fieldSelector[reportType]);
      setTotalField(totalFieldSelector[reportType]);
      dispatch(
        GENERATE_REPORT({
          successCallback: (total) =>
            sendEventToPostHog(POSTHOG_EVENT_NAMES.REPORTS_GENERATED, total)
        })
      );
    }
  };

  // Apply URL params (type/period) on mount — runs before useDidMountEffect so Redux
  // state is already correct when GENERATE_REPORT fires.
  useEffect(() => {
    const urlParams = new URLSearchParams(initialLocationSearchRef.current);
    const typeParam = urlParams.get('type');
    const periodParam = urlParams.get('period');
    if (typeParam === null && periodParam === null) return;

    const typeNum = typeParam !== null ? Number(typeParam) : null;
    const periodNum = periodParam !== null ? Number(periodParam) : null;

    if (typeNum !== null && !Number.isNaN(typeNum)) {
      dispatch(SET_REPORT_TYPE(typeNum));
      setFields(fieldSelector[typeNum]);
      setTotalField(totalFieldSelector[typeNum]);
    }
    if (periodNum !== null && !Number.isNaN(periodNum)) {
      dispatch(SET_TIME_PERIOD(periodNum));
      dispatch(SET_DATEPICKER_INDEX(periodNum));
      dispatch(SET_REPORTS_DATE_INTERVAL(getDateRangeForPeriod(periodNum)));
    }
    history.replace(location.pathname);
  }, []);

  useEffect(() => {
    if (
      location?.state ||
      (location?.state == null &&
        !Object.keys(previousLocationState || {}).length)
    ) {
      setPreviousLocationState(location.state ?? undefined);
    }
  }, [location?.state]);

  useDidMountEffect(() => {
    if (
      previousLocationState?.fromFeedback &&
      previousLocationState?.datePickerConfig
    ) {
      const {
        startDate,
        endDate,
        datePickerSelectIndex
      } = previousLocationState?.datePickerConfig;
      dispatch(SET_REPORT_TYPE(6));
      dispatch(SET_REPORTS_DATE_INTERVAL([startDate, endDate]));
      dispatch(SET_DATEPICKER_INDEX(datePickerSelectIndex));
    } else {
      const initialParams = new URLSearchParams(
        initialLocationSearchRef.current
      );
      if (initialParams.get('period') === null) {
        // No URL params — apply default date range
        dispatch(SET_REPORTS_DATE_INTERVAL(defaultDateArr));
      }
      dispatch(
        GENERATE_REPORT({
          successCallback: (total) =>
            sendEventToPostHog(POSTHOG_EVENT_NAMES.REPORTS_GENERATED, total)
        })
      );
    }
  }, [previousLocationState]);

  useDidMountEffect(() => {
    if (
      reportType === 6 &&
      !initialReq &&
      previousLocationState?.fromFeedback
    ) {
      generateButtonHandler();
      setInitialReq(() => true);
    }
  }, [reportType]);

  useEffect(() => {
    return function resetSettings() {
      dispatch(SET_REPORT_VISITED_STATISTIC(null));
      dispatch(SET_REPORT_TYPE(2));
      dispatch(SET_REPORTS_DATE_INTERVAL(defaultDateArr));
      dispatch(SET_DATEPICKER_INDEX(3));
      dispatch(SET_TIME_PERIOD(3));
      dispatch(
        SET_SELF_DELIVERY_DATE_FILTER(SELF_DELIVERY_DATE_FILTER.ORDER_DATE)
      );
    };
  }, []);

  // useEffect(() => {
  //   dispatch(GET_REPORTS());
  // }, [skip]);

  return (
    <>
      <DashboardContent className='DashboardContent'>
        {previousLocationState?.fromFeedback && (
          <TopLineNav>
            <LinkBack
              className='marginBottomXs'
              onClick={() => {
                history.push('/feedback');
              }}
            >
              Back to Reviews
            </LinkBack>
          </TopLineNav>
        )}
        <DashboardHeader className='alwaysFlex flexWrap'>
          <div>
            <Headline>Reports</Headline>
          </div>
          <Box
            display='flex'
            flexWrap='wrap'
            justifyContent='flex-end'
            alignItems='center'
            gap='12px'
            style={{minHeight: '46px'}}
          >
            {((reportType === REPORT_TYPES.SELF_DELIVERY_SUMMARY &&
              reportType === data.reportTypeInner &&
              !selfDeliveryViewMismatch &&
              dateRange[0] === data.reportPeriod[0] &&
              dateRange[1] === data.reportPeriod[1]) ||
              (reportType === REPORT_TYPES.SELF_DELIVERY_INVOICES &&
                reportType === data.reportTypeInner)) &&
              data.dataRows &&
              data.dataRows.length > 0 && (
                <Box
                  textAlign='center'
                  display='flex'
                  justifyContent='center'
                  alignItems='center'
                  style={{
                    width: '188px',
                    minHeight: '46px',
                    marginRight: '12px'
                  }}
                >
                  {settingsButtonLoading ? (
                    <Loading className='LoadingExportButton' />
                  ) : (
                    <ReportButtonPrimary
                      style={{paddingRight: '45px', paddingLeft: '45px'}}
                      data-gtm='settingsButton'
                      primary
                      onClick={handleSettingsClick}
                    >
                      <div>
                        <IconSettingsWhite />
                      </div>
                      <span data-gtm='settingsButton'>Settings</span>
                    </ReportButtonPrimary>
                  )}
                </Box>
              )}
            {bulkExportEnabled && (
              <Box
                textAlign='center'
                style={{width: '188px', minHeight: '46px', marginRight: '12px'}}
              >
                <ReportButtonOutline
                  data-gtm='bulkExportButton'
                  onClick={() => {
                    sendEventToPostHog(
                      POSTHOG_EVENT_NAMES.REPORTS_BULK_EXPORT_CLICKED,
                      null
                    );
                    setBulkExportOpen(true);
                  }}
                >
                  <span data-gtm='bulkExportButton'>Bulk export</span>
                </ReportButtonOutline>
              </Box>
            )}
            {reportType !== REPORT_TYPES.SELF_DELIVERY_INVOICES && (
              <Box
                textAlign='center'
                style={{width: '188px', minHeight: '46px'}}
              >
                {exportLoading ? (
                  <Loading className='LoadingExportButton' />
                ) : (
                  <ReportButtonPrimary
                    className={cn({
                      disabled:
                        reportType !== data.reportTypeInner ||
                        selfDeliveryViewMismatch ||
                        dateRange[0] !== data.reportPeriod[0] ||
                        dateRange[1] !== data.reportPeriod[1] ||
                        (reportType === REPORT_TYPES.SALES_BREAKDOWN_SUMMARY &&
                          (!selectionsMatch(
                            salesBreakdownChannels,
                            data.channels
                          ) ||
                            !selectionsMatch(
                              salesBreakdownFulfillments,
                              data.fulfillments
                            ))) ||
                        (data.dataRows && data.dataRows.length < 1) ||
                        reportType === REPORT_TYPES.SPECIAL_ORDERS
                    })}
                    disabled={selfDeliveryViewMismatch}
                    data-gtm='exportAReportButton'
                    primary
                    onClick={() => {
                      sendEventToPostHog(
                        POSTHOG_EVENT_NAMES.REPORTS_EXPORT_CLICKED,
                        null
                      );
                      dispatch(EXPORT_REPORTS());
                    }}
                  >
                    <div>
                      <IconExportReport />
                    </div>
                    <span data-gtm='exportAReportButton'>Export</span>
                  </ReportButtonPrimary>
                )}
              </Box>
            )}
          </Box>
        </DashboardHeader>
        <DashboardInner className='paddingReportsDataPicker'>
          <ReportsDataPicker
            generateButtonHandler={generateButtonHandler}
            setIncludeModifiers={setIncludeModifiers}
            includeModifiers={includeModifiers}
          />
        </DashboardInner>
        {generationLoading && <FullScreenLoading />}
        {!generationLoading && reportType !== REPORT_TYPES.SPECIAL_ORDERS && (
          <ReportsTable
            loading={dataLoading}
            fields={fields}
            dataObject={data}
            totalField={totalField}
            totalRow={totalRow}
            dataCount={dataCount}
          />
        )}
        {!generationLoading && reportType === REPORT_TYPES.SPECIAL_ORDERS && (
          <SpecialOrdersReport specialOrdersReportId={specialOrdersReportId} />
        )}
      </DashboardContent>
      {isCheckStripeAccountExpress &&
        !employeeView &&
        !window.AndroidDashboard &&
        quantityData === 0 && <ModalReportVideo />}
      {locationId && (
        <PlatformCommissionsSettingsModal
          open={settingsModalOpen}
          onClose={() => setSettingsModalOpen(false)}
          locationId={locationId}
          initialCommissions={initialCommissionsForModal}
          onSaveSuccess={(commissions) =>
            dispatch(RECALCULATE_COMMISSION_SAVED(commissions))
          }
        />
      )}
      {bulkExportEnabled && (
        <BulkExportModal
          open={bulkExportOpen}
          locations={userLocations}
          email={userData?.email}
          timezone={Timezone}
          onClose={() => setBulkExportOpen(false)}
          onSent={(result) => {
            sendEventToPostHog(
              POSTHOG_EVENT_NAMES.REPORTS_BULK_EXPORT_SENT,
              null
            );
            setBulkExportOpen(false);
            setBulkExportResult(result);
          }}
        />
      )}
      <BulkExportSentModal
        open={Boolean(bulkExportResult)}
        email={userData?.email}
        emailSubject={bulkExportResult?.emailSubject}
        alreadyInProgress={bulkExportResult?.alreadyInProgress}
        onClose={() => setBulkExportResult(null)}
      />
    </>
  );
};

export default ReportsContainer;
